import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { createSharedLink } from './create-shared-link';
import { deleteSharedLink } from './delete-shared-link';
import { sharedLinkQueryKeys } from './queries';
import type { SharedLink } from './types';
import { updateSharedLink } from './update-shared-link';
import { verifySharedLink } from './verify-shared-link';

// Create
type CreateSharedLinkParams = Parameters<typeof createSharedLink>[0];
type UseCreateSharedLinkMutationOptions = Omit<
  UseMutationOptions<SharedLink, Error, CreateSharedLinkParams>,
  'mutationFn'
>;

export const useCreateSharedLinkMutation = (
  options?: UseCreateSharedLinkMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createSharedLink,
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: sharedLinkQueryKeys.all });
      queryClient.setQueryData(sharedLinkQueryKeys.detail(data.id), data);
    },
    ...options,
  });
};

// Update
type UpdateSharedLinkParams = Parameters<typeof updateSharedLink>[0];
type UseUpdateSharedLinkMutationOptions = Omit<
  UseMutationOptions<SharedLink, Error, UpdateSharedLinkParams>,
  'mutationFn'
>;

export const useUpdateSharedLinkMutation = (
  options?: UseUpdateSharedLinkMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateSharedLink,
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        sharedLinkQueryKeys.detail(variables.linkId),
        data,
      );
      void queryClient.invalidateQueries({ queryKey: sharedLinkQueryKeys.all });
    },
    ...options,
  });
};

// Delete
type DeleteSharedLinkParams = Parameters<typeof deleteSharedLink>[0];
type UseDeleteSharedLinkMutationOptions = Omit<
  UseMutationOptions<void, Error, DeleteSharedLinkParams>,
  'mutationFn'
>;

export const useDeleteSharedLinkMutation = (
  options?: UseDeleteSharedLinkMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteSharedLink,
    onSuccess: (_, variables) => {
      queryClient.removeQueries({
        queryKey: sharedLinkQueryKeys.detail(variables.linkId),
      });
      void queryClient.invalidateQueries({ queryKey: sharedLinkQueryKeys.all });
    },
    ...options,
  });
};

// Verify
type VerifySharedLinkParams = Parameters<typeof verifySharedLink>[0];
type UseVerifySharedLinkMutationOptions = Omit<
  UseMutationOptions<Record<string, never>, Error, VerifySharedLinkParams>,
  'mutationFn'
>;

export const useVerifySharedLinkMutation = (
  options?: UseVerifySharedLinkMutationOptions,
) => {
  return useMutation({
    mutationFn: verifySharedLink,
    ...options,
  });
};
